import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';

const I18nContext = createContext(null);

export const SUPPORTED_LANGUAGES = ['en', 'nl', 'fr', 'de'];
const FALLBACK_LANGUAGE = 'en';

// Lazy glob: every value in this map is a function that dynamically imports
// one locale JSON file, so Vite code-splits each language/namespace into its
// own chunk instead of bundling every translation up front.
const localeLoaders = import.meta.glob('/src/locales/*/*.json');

function interpolate(value, vars) {
  if (!vars || typeof value !== 'string') return value;
  return value.replace(/\{\{(\w+)\}\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
  );
}

// Dev-only, deduped-per-key missing-translation warning (Phase 20) -- the
// fallback chain itself (active language -> English -> raw key) is
// unchanged and stays completely silent in production; import.meta.env.DEV
// is statically known at build time, so this whole branch (including the
// Set) is dead-code-eliminated from a production bundle.
const warnedMissingKeys = new Set();
function warnMissingKeyOnce(namespace, key) {
  const cacheKey = `${namespace}:${key}`;
  if (warnedMissingKeys.has(cacheKey)) return;
  warnedMissingKeys.add(cacheKey);
  console.warn(`[i18n] Missing translation key "${key}" in namespace "${namespace}" (checked active language and English fallback).`);
}

function detectLanguage(user, tenant) {
  const candidate = user?.language || tenant?.defaultLanguage || FALLBACK_LANGUAGE;
  return SUPPORTED_LANGUAGES.includes(candidate) ? candidate : FALLBACK_LANGUAGE;
}

/**
 * Custom i18n provider (no i18next/react-intl). Namespaces (one JSON file
 * per module, e.g. "common", "customers", "vehicles") are loaded on demand
 * per active language and cached; t(namespace, key, vars) falls back
 * language -> English -> the raw key itself so the UI never shows "undefined".
 */
export function I18nProvider({ children }) {
  const { user, tenant } = useAuth();
  const [language, setLanguageState] = useState(() => detectLanguage(user, tenant));
  const [dicts, setDicts] = useState({});
  const pendingRef = useRef(new Set());

  const ensureNamespace = useCallback(
    (namespace, lang) => {
      const targetLang = lang || language;
      const cacheKey = `${targetLang}:${namespace}`;
      if (pendingRef.current.has(cacheKey)) return;

      pendingRef.current.add(cacheKey);
      setDicts((prev) => {
        if (prev[targetLang]?.[namespace]) {
          pendingRef.current.delete(cacheKey);
          return prev;
        }
        const path = `/src/locales/${targetLang}/${namespace}.json`;
        const loader = localeLoaders[path];
        if (!loader) {
          return prev;
        }
        loader()
          .then((mod) => {
            setDicts((current) => ({
              ...current,
              [targetLang]: { ...current[targetLang], [namespace]: mod.default },
            }));
          })
          .finally(() => {
            pendingRef.current.delete(cacheKey);
          });
        return prev;
      });
    },
    [language]
  );

  // "common" is used by the app shell everywhere, so keep it (and its
  // English fallback) always loaded for the active language.
  useEffect(() => {
    ensureNamespace('common', language);
    if (language !== FALLBACK_LANGUAGE) ensureNamespace('common', FALLBACK_LANGUAGE);
  }, [language, ensureNamespace]);

  // Re-derive the active language whenever the authenticated user/tenant
  // resolves or changes (e.g. right after login, or /me on page reload).
  useEffect(() => {
    setLanguageState(detectLanguage(user, tenant));
  }, [user?.language, tenant?.defaultLanguage]);

  const setLanguage = useCallback((lang) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return;
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (namespace, key, vars) => {
      ensureNamespace(namespace, language);
      if (language !== FALLBACK_LANGUAGE) ensureNamespace(namespace, FALLBACK_LANGUAGE);

      const active = dicts[language]?.[namespace]?.[key];
      if (active !== undefined) return interpolate(active, vars);

      const fallback = dicts[FALLBACK_LANGUAGE]?.[namespace]?.[key];
      if (fallback !== undefined) return interpolate(fallback, vars);

      // A namespace not yet finished loading (its dict entry is still
      // `undefined`, not just missing this one key) looks identical to a
      // genuinely missing key on the very first render -- only warn once
      // BOTH the active-language and English dicts have actually loaded,
      // so a page's first paint (before the async locale fetch resolves)
      // never produces a false-positive "missing key" for a key that's
      // really just not loaded yet.
      const activeNamespaceLoaded = dicts[language]?.[namespace] !== undefined;
      const fallbackNamespaceLoaded = language === FALLBACK_LANGUAGE || dicts[FALLBACK_LANGUAGE]?.[namespace] !== undefined;
      if (import.meta.env.DEV && activeNamespaceLoaded && fallbackNamespaceLoaded) {
        warnMissingKeyOnce(namespace, key);
      }
      return key;
    },
    [dicts, language, ensureNamespace]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      ensureNamespace,
      supportedLanguages: SUPPORTED_LANGUAGES,
    }),
    [language, setLanguage, t, ensureNamespace]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

/**
 * Convenience hook for module pages: ensures a namespace starts loading as
 * soon as the page mounts (or the language changes), rather than waiting on
 * the first render's t() call to kick it off.
 */
export function useNamespace(namespace) {
  const { ensureNamespace, language } = useI18n();
  useEffect(() => {
    ensureNamespace(namespace, language);
  }, [namespace, language, ensureNamespace]);
}
